import { fetchCurrentUser } from '../../ducks/user.duck';
import {
    fetchFreeTransactions,
    getAdminPersonaDetailsApi,
    getPersonaUserDetailsApi,
    updateAdminDefaultConfigApi,
} from '../../util/api';
import { isCurrentUserAdmin } from '../../util/constants';
import moment from 'moment-timezone/builds/moment-timezone-with-data-10-year-range.min';
import { parse } from '../../util/urlHelpers';
import { isRefunded } from '../../transactions/transactionProcessBookingFree';
import { storableError } from '../../util/errors';

// ================ Action types ================ //

export const FETCH_TRANSACTION_DATA_REQUEST =
    'app/AdminWalletPage/FETCH_TRANSACTION_DATA_REQUEST';
export const FETCH_TRANSACTION_DATA_SUCCESS =
    'app/AdminWalletPage/FETCH_TRANSACTION_DATA_SUCCESS';
export const FETCH_TRANSACTION_DATA_ERROR =
    'app/AdminWalletPage/FETCH_TRANSACTION_DATA_ERROR';

export const FETCH_ADMIN_CONFIG_REQUEST =
    'app/AdminWalletPage/FETCH_ADMIN_CONFIG_REQUEST';
export const FETCH_ADMIN_CONFIG_SUCCESS =
    'app/AdminWalletPage/FETCH_ADMIN_CONFIG_SUCCESS';
export const FETCH_ADMIN_CONFIG_ERROR =
    'app/AdminWalletPage/FETCH_ADMIN_CONFIG_ERROR';

export const UPDATE_ADMIN_DEFAULT_CONFIG_REQUEST =
    'app/AdminWalletPage/UPDATE_ADMIN_DEFAULT_CONFIG_REQUEST';
export const UPDATE_ADMIN_DEFAULT_CONFIG_SUCCESS =
    'app/AdminWalletPage/UPDATE_ADMIN_DEFAULT_CONFIG_SUCCESS';
export const UPDATE_ADMIN_DEFAULT_CONFIG_ERROR =
    'app/AdminWalletPage/UPDATE_ADMIN_DEFAULT_CONFIG_ERROR';

export const GET_PERSONA_USER_DETAILS_REQUEST =
    'app/AdminWalletPage/GET_PERSONA_USER_DETAILS_REQUEST';
export const GET_PERSONA_USER_DETAILS_SUCCESS =
    'app/AdminWalletPage/GET_PERSONA_USER_DETAILS_SUCCESS';
export const GET_PERSONA_USER_DETAILS_ERROR =
    'app/AdminWalletPage/GET_PERSONA_USER_DETAILS_ERROR';

export const SET_CURRENT_TAB = 'app/AdminWalletPage/SET_CURRENT_TAB';

// ================ Reducer ================ //

const initialState = {
    currentTab: 'wallet-management',

    transactionData: [],
    fetchTransactionInProgress: false,
    fetchTransactionError: null,

    adminConfig: null,
    fetchAdminConfigInProgress: false,
    fetchAdminConfigError: null,

    // Pagination for persona user details will be inside the personaUserDetails object
    personaUserDetails: null,
    getPersonaUserDetailsInProgress: false,
    getPersonaUserDetailsError: null,

    pagination: {
        page: 1,
        perPage: 100,
        totalItems: 0,
        totalPages: 0,
    },
    totalPlatFormAmount: 0,
    baseCurrency: 'USD',

    updateAdminDefaultConfigInProgress: false,
    updateAdminDefaultConfigError: null,
};

export default function reducer(state = initialState, action = {}) {
    const { type, payload } = action;
    switch (type) {
        case FETCH_TRANSACTION_DATA_REQUEST:
            return {
                ...state,
                fetchTransactionInProgress: true,
                fetchTransactionError: null,
            };
        case FETCH_TRANSACTION_DATA_SUCCESS:
            return {
                ...state,
                fetchTransactionInProgress: false,
                transactionData: payload.transactionData,
                pagination: payload.pagination,
                totalPlatFormAmount: payload.totalPlatFormAmount || 0,
                baseCurrency: payload.baseCurrency || 'USD',
            };
        case FETCH_TRANSACTION_DATA_ERROR:
            return {
                ...state,
                fetchTransactionInProgress: false,
                fetchTransactionError: payload,
            };
        case FETCH_ADMIN_CONFIG_REQUEST:
            return {
                ...state,
                fetchAdminConfigInProgress: true,
                fetchAdminConfigError: null,
            };
        case FETCH_ADMIN_CONFIG_SUCCESS:
            return {
                ...state,
                fetchAdminConfigInProgress: false,
                adminConfig: payload,
                fetchAdminConfigError: null,
            };
        case FETCH_ADMIN_CONFIG_ERROR:
            return {
                ...state,
                fetchAdminConfigInProgress: false,
                fetchAdminConfigError: payload,
            };
        case SET_CURRENT_TAB:
            return { ...state, currentTab: payload };
        case UPDATE_ADMIN_DEFAULT_CONFIG_REQUEST:
            return {
                ...state,
                fetchAdminConfigInProgress: true,
                updateAdminDefaultConfigError: null,
            };
        case UPDATE_ADMIN_DEFAULT_CONFIG_SUCCESS:
            return {
                ...state,
                fetchAdminConfigInProgress: false,
                adminConfig: payload.data,
                fetchAdminConfigError: null,
            };
        case UPDATE_ADMIN_DEFAULT_CONFIG_ERROR:
            return {
                ...state,
                fetchAdminConfigInProgress: false,
                fetchAdminConfigError: payload,
            };
        case GET_PERSONA_USER_DETAILS_REQUEST:
            return {
                ...state,
                getPersonaUserDetailsInProgress: true,
                getPersonaUserDetailsError: null,
            };
        case GET_PERSONA_USER_DETAILS_SUCCESS:
            // The response structure is: { data: { data: [...], meta: {...} }, meta: {...} }
            // So we need to extract from payload.data.data
            const responseData = payload.data || {};
            const responseMeta = responseData.meta || payload.meta || {};
            
            // Extract the data array - check nested structure first
            let userData = [];
            if (Array.isArray(responseData.data)) {
                userData = responseData.data;
            } else if (Array.isArray(payload.data)) {
                userData = payload.data;
            } else if (Array.isArray(payload)) {
                userData = payload;
            } else {
                console.warn('Could not find data array in payload:', payload);
            }
            
            return {
                ...state,
                getPersonaUserDetailsInProgress: false,
                personaUserDetails: userData,
                pagination: {
                    page: responseMeta.page || state.pagination.page,
                    perPage: responseMeta.perPage || state.pagination.perPage,
                    totalItems: responseMeta.totalItems || 0,
                    totalPages: responseMeta.totalPages || 0,
                },
                getPersonaUserDetailsError: null,
            };
        case GET_PERSONA_USER_DETAILS_ERROR:
            return {
                ...state,
                getPersonaUserDetailsInProgress: false,
                getPersonaUserDetailsError: payload,
            };
        default:
            return state;
    }
}

// ================ Action creators ================ //
export const setCurrentTab = tab => ({
    type: SET_CURRENT_TAB,
    payload: tab,
});
export const fetchTransactionDataRequest = () => ({
    type: FETCH_TRANSACTION_DATA_REQUEST,
});
export const fetchTransactionDataError = error => ({
    type: FETCH_TRANSACTION_DATA_ERROR,
    payload: error,
});
export const fetchTransactionDataSuccess = data => ({
    type: FETCH_TRANSACTION_DATA_SUCCESS,
    payload: data,
});
export const fetchAdminConfigRequest = () => ({
    type: FETCH_ADMIN_CONFIG_REQUEST,
});
export const fetchAdminConfigSuccess = data => ({
    type: FETCH_ADMIN_CONFIG_SUCCESS,
    payload: data,
});
export const fetchAdminConfigError = error => ({
    type: FETCH_ADMIN_CONFIG_ERROR,
    payload: error,
});
export const updateAdminDefaultConfigRequest = () => ({
    type: UPDATE_ADMIN_DEFAULT_CONFIG_REQUEST,
});
export const updateAdminDefaultConfigSuccess = data => ({
    type: UPDATE_ADMIN_DEFAULT_CONFIG_SUCCESS,
    payload: data,
});
export const updateAdminDefaultConfigError = error => ({
    type: UPDATE_ADMIN_DEFAULT_CONFIG_ERROR,
    payload: error,
});
export const getPersonaUserDetailsRequest = () => ({
    type: GET_PERSONA_USER_DETAILS_REQUEST,
});
export const getPersonaUserDetailsSuccess = data => ({
    type: GET_PERSONA_USER_DETAILS_SUCCESS,
    payload: data,
});
export const getPersonaUserDetailsError = error => ({
    type: GET_PERSONA_USER_DETAILS_ERROR,
    payload: error,
});
// ================ Thunks ================ //

// Transform API response data into mock data format
const transformTransactionData = apiResponse => {
    if (!apiResponse || !apiResponse.data || !Array.isArray(apiResponse.data)) {
        return [];
    }

    const transformedData = apiResponse.data.map(transaction => {
        // Divide amount by 100 (convert from cents to dollars)
        const amount = transaction.tx_amount ? transaction.tx_amount / 100 : 0;

        // Format date - use createdAt from mongoose timestamps, or current date as fallback
        const dateObj = transaction.createdAt
            ? new Date(transaction.createdAt)
            : transaction.updatedAt
                ? new Date(transaction.updatedAt)
                : new Date();
        const formattedDate = moment(dateObj).format('MMM D, YYYY');

        const status = isRefunded(transaction.tx_status) ? 'refunded' : 'succeeded';

        // Get transaction ID - use tx_id or generate from MongoDB _id
        const transactionId = transaction.tx_id
            ? `#${transaction.tx_id.slice(-4)}`
            : transaction._id
                ? `#${transaction._id.toString().slice(-4)}`
                : '#0000';

        // Default payment method is Stripe
        const paymentMethod = {
            type: 'Stripe',
            last4: transaction.payment_intent_id
                ? transaction.payment_intent_id.slice(-4)
                : transaction.tx_id
                    ? transaction.tx_id.slice(-4)
                    : '0000',
        };

        return {
            id: transactionId,
            currency: transaction.tx_currency || 'USD',
            amount: parseFloat(amount.toFixed(2)),
            status: status,
            date: formattedDate,
            paymentMethod: paymentMethod,
        };
    });

    return transformedData;
};

export const fetchFreeTransactionsForUser = (payload, currentPage, perPage) => (
    dispatch,
    getState,
    sdk
) => {
    // Pass pageParams to API
    return fetchFreeTransactions({
        payload: payload,
        pageParams: {
            page: currentPage,
            perPage: perPage,
        },
    })
        .then(response => {
            // Transform the API response data
            return response;
        })
        .catch(error => {
            console.log('error', error);
        });
};

export const getAdminConfig = () => async (dispatch, getState, sdk) => {
    try {
        dispatch(fetchAdminConfigRequest());
        const response = await getAdminPersonaDetailsApi();
        dispatch(fetchAdminConfigSuccess(response.data));
    } catch (error) {
        console.error(error);
        dispatch(fetchAdminConfigError(storableError(error)));
    }
};
export const updateAdminDefaultConfig = body => async (
    dispatch,
    getState,
    sdk
) => {
    try {
        dispatch(updateAdminDefaultConfigRequest());
        const response = await updateAdminDefaultConfigApi(body);
        dispatch(updateAdminDefaultConfigSuccess(response.data));
    } catch (error) {
        console.error(error);
        dispatch(updateAdminDefaultConfigError(storableError(error)));
    }
};
export const getPersonaUserDetails = (page = 1, perPage = 10) => async (dispatch, getState, sdk) => {
    try {
        dispatch(getPersonaUserDetailsRequest());
        const response = await getPersonaUserDetailsApi({ page, perPage });
        dispatch(getPersonaUserDetailsSuccess(response));
    } catch (error) {
        console.error(error);
        dispatch(getPersonaUserDetailsError(storableError(error)));
    }
};
export const loadData = (params, search) => (dispatch, getState, sdk) => {
    const { tab } = params;
    if (tab) {
        dispatch(setCurrentTab(tab));
    }
    dispatch(fetchTransactionDataRequest());

    // Parse search params to get page number
    const searchParams = search ? parse(search) : {};
    const currentPage = searchParams.page ? parseInt(searchParams.page, 10) : 1;
    const perPage = 10;

    return dispatch(fetchCurrentUser())
        .then(response => {
            const currentUser = getState().user.currentUser;
            const isAdmin = isCurrentUserAdmin(currentUser);

            if (!isAdmin) {
                dispatch(fetchTransactionDataError('User is not admin'));
                return Promise.reject(new Error('User is not admin'));
            }

            // Pass pageParams to API
            if (tab === 'trust-verification' || tab === 'persona-details') {
                dispatch(getAdminConfig());
            }
            if( tab === 'persona-details') {
                dispatch(getPersonaUserDetails(currentPage, perPage));
            }
            if (tab === 'wallet-management') {
                fetchFreeTransactions({
                    payload: {},
                    isAdmin,
                    pageParams: {
                        page: currentPage,
                        perPage: perPage,
                    },
                })
                    .then(response => {
                        // Transform the API response data
                        const transformedData = transformTransactionData(response);

                        // Extract pagination info from API response
                        const pagination = response.pagination || {
                            page: currentPage,
                            perPage: perPage,
                            totalItems: 0,
                            totalPages: 0,
                        };

                        dispatch(
                            fetchTransactionDataSuccess({
                                transactionData: transformedData,
                                pagination: pagination,
                                totalPlatFormAmount: response.totalPlatFormAmount || 0,
                                baseCurrency: response.baseCurrency || 'USD',
                            })
                        );

                        return {
                            transactionData: transformedData,
                            pagination: pagination,
                            totalPlatFormAmount: response.totalPlatFormAmount || 0,
                        };
                    })
                    .catch(error => {
                        dispatch(fetchTransactionDataError(error));
                        throw error;
                    });
            }
            return;
        })
        .catch(error => {
            dispatch(fetchTransactionDataError(error));
            throw error;
        });
};
